import { Loader2 } from 'lucide-react';

import {
  buildCoachOverlayStubLines,
  buildLearnerOverlayStubLines,
  overlayInkRoleLabel,
  overlayKindLabel,
  type CoachOverlayDisplay,
  type LearnerOverlayDisplay,
  type OverlayAnnotationDisplay,
  type OverlayLayerKind,
} from '../utils/particleOverlayDisplay';
import { MathTextBlock } from './MathTextBlock';
import { FloatingDetailCard } from './KnowledgeParticlePapDetailModal';

export function KnowledgeParticleOverlayStubPanel({
  layer,
  coachDisplay = null,
  learnerDisplay = null,
  extracting = false,
  extractError = null,
}: {
  layer: OverlayLayerKind;
  coachDisplay?: CoachOverlayDisplay | null;
  learnerDisplay?: LearnerOverlayDisplay | null;
  extracting?: boolean;
  extractError?: string | null;
}) {
  const isCoach = layer === 'coach';
  const display = isCoach ? coachDisplay : learnerDisplay;
  const lines = isCoach
    ? buildCoachOverlayStubLines(coachDisplay)
    : buildLearnerOverlayStubLines(learnerDisplay);
  const title = isCoach ? '教练层' : '学习层';
  const emptyText = isCoach ? '暂无教练层内容。' : '暂无学习层内容。';
  const extractingText = isCoach ? '正在提取教练层…' : '正在提取学习层…';
  const hasContent = Boolean(display?.hasContent && lines.length);
  const showExtracting = extracting && !hasContent;
  const showError = Boolean(extractError) && !showExtracting && !hasContent;
  const stateText = showExtracting ? extractingText : showError ? extractError : emptyText;

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col">
      <div className={`border-b border-black/10 px-2 py-1.5 ${isCoach ? 'bg-[#F6E4D0]' : 'bg-[#D5F0EC]'}`}>
        <p className="text-[11px] font-extrabold tracking-wide text-ink">{title}</p>
      </div>
      <div className="min-h-0 flex-1 space-y-1 overflow-hidden px-2 py-2">
        {showExtracting ? (
          <div className="flex items-center gap-1.5 text-[11px] leading-4 text-muted">
            <Loader2 size={12} className={`shrink-0 animate-spin ${isCoach ? 'text-[#B45309]' : 'text-[#0D9488]'}`} />
            <span>{extractingText}</span>
          </div>
        ) : hasContent ? (
          lines.map((line) => (
            <div key={line.key} className="flex items-start gap-1.5">
              <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${isCoach ? 'bg-[#B45309]' : 'bg-[#0D9488]'}`} />
              <MathTextBlock text={line.text} className="min-w-0 text-[11px] leading-4 text-ink" />
            </div>
          ))
        ) : (
          <p className={`text-[11px] leading-4 ${showError ? 'text-danger' : 'text-muted'}`}>{stateText}</p>
        )}
      </div>
    </div>
  );
}

function AnnotationBlock({ items }: { items: OverlayAnnotationDisplay[] }) {
  if (!items.length) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-extrabold text-ink">标注点</h3>
      {items.map((ann, index) => {
        const kindZh = overlayKindLabel(ann.kind);
        const roleZh = overlayInkRoleLabel(ann.inkRole);
        const title = ann.label || kindZh || '标注';
        return (
          <div key={`${ann.id}-${index}`} className="rounded-lg border border-border bg-surface p-3">
            <p className="text-sm font-bold text-ink">
              {index + 1}. {title}{roleZh ? ` · ${roleZh}` : ''}
            </p>
            {kindZh && kindZh !== title ? <p className="mt-1 text-xs text-muted">类型：{kindZh}</p> : null}
            {ann.text ? <p className="mt-1 text-xs leading-relaxed text-ink">{ann.text}</p> : null}
          </div>
        );
      })}
    </section>
  );
}

function OverlayDetailBody({
  layer,
  coachDisplay,
  learnerDisplay,
}: {
  layer: OverlayLayerKind;
  coachDisplay?: CoachOverlayDisplay | null;
  learnerDisplay?: LearnerOverlayDisplay | null;
}) {
  if (layer === 'coach') {
    const display = coachDisplay;
    if (!display?.hasContent) {
      return <p className="text-sm text-muted">暂未识别到可展示的教练层内容。</p>;
    }
    return (
      <div className="space-y-4">
        {display.admonitions.length ? (
          <section className="space-y-2">
            <h3 className="text-sm font-extrabold text-ink">嘱咐</h3>
            {display.admonitions.map((item, index) => (
              <div key={`adm-${index}`} className="rounded-lg bg-[#EAF2FF] px-3 py-2">
                <MathTextBlock text={item.text} className="text-sm text-ink" />
              </div>
            ))}
          </section>
        ) : null}
        {display.traps.length ? (
          <section className="space-y-2">
            <h3 className="text-sm font-extrabold text-ink">陷阱</h3>
            {display.traps.map((trap, index) => (
              <div key={`trap-${index}`} className="rounded-lg border border-border p-3">
                <p className="text-sm font-bold text-ink">{index + 1}. {trap.title || '陷阱'}</p>
                {trap.wrongHint ? <p className="mt-1 text-xs text-ink">易错：{trap.wrongHint}</p> : null}
                {trap.rightHint ? <p className="mt-1 text-xs text-muted">正解：{trap.rightHint}</p> : null}
              </div>
            ))}
          </section>
        ) : null}
        {display.strategyTags.length ? (
          <section className="space-y-2">
            <h3 className="text-sm font-extrabold text-ink">策略标签</h3>
            <div className="flex flex-wrap gap-2">
              {display.strategyTags.map((tag) => (
                <span key={tag} className="rounded-full bg-[#EAF2FF] px-2.5 py-1 text-xs text-ink">{tag}</span>
              ))}
            </div>
          </section>
        ) : null}
        {display.identityBank.length ? (
          <section className="space-y-2">
            <h3 className="text-sm font-extrabold text-ink">恒等式</h3>
            {display.identityBank.map((item, index) => (
              <MathTextBlock key={`id-${index}`} text={item.expr} className="text-sm text-ink" />
            ))}
          </section>
        ) : null}
        {display.methodVariants.length ? (
          <section className="space-y-2">
            <h3 className="text-sm font-extrabold text-ink">手法变体</h3>
            {display.methodVariants.map((item, index) => (
              <div key={`mv-${index}`} className="rounded-lg border border-border p-3">
                <p className="text-sm font-bold text-ink">{item.problemRef || item.methods[0] || '手法'}</p>
                {item.methods.map((method) => (
                  <p key={method} className="mt-1 text-xs text-ink">{method}</p>
                ))}
              </div>
            ))}
          </section>
        ) : null}
        <AnnotationBlock items={display.annotations} />
      </div>
    );
  }

  const display = learnerDisplay;
  if (!display?.hasContent) {
    return <p className="text-sm text-muted">暂未识别到可展示的学习层内容。</p>;
  }
  return (
    <div className="space-y-4">
      {display.struggleSignals.length ? (
        <section className="space-y-2">
          <h3 className="text-sm font-extrabold text-ink">挣扎信号</h3>
          {display.struggleSignals.map((item, index) => (
            <div key={`sg-${index}`} className="rounded-lg border border-border p-3">
              <p className="text-sm font-bold text-ink">
                {index + 1}. {overlayKindLabel(item.kind) || '信号'}
              </p>
              {item.summary ? <p className="mt-1 text-xs leading-relaxed text-ink">{item.summary}</p> : null}
            </div>
          ))}
        </section>
      ) : null}
      {display.scratchPaths.length ? (
        <section className="space-y-2">
          <h3 className="text-sm font-extrabold text-ink">草稿路径</h3>
          {display.scratchPaths.map((item, index) => (
            <div key={`sp-${index}`} className="rounded-lg border border-border p-3">
              <p className="text-sm text-ink">{item.summary}</p>
            </div>
          ))}
        </section>
      ) : null}
      <AnnotationBlock items={display.annotations} />
    </div>
  );
}

export function KnowledgeParticleOverlayDetailModal({
  visible,
  onClose,
  layer,
  coachDisplay = null,
  learnerDisplay = null,
  extracting = false,
  extractError = null,
  onExtract,
}: {
  visible: boolean;
  onClose: () => void;
  layer: OverlayLayerKind;
  coachDisplay?: CoachOverlayDisplay | null;
  learnerDisplay?: LearnerOverlayDisplay | null;
  extracting?: boolean;
  extractError?: string | null;
  onExtract?: () => void;
}) {
  if (!visible) return null;
  const isCoach = layer === 'coach';
  const hasContent = isCoach ? Boolean(coachDisplay?.hasContent) : Boolean(learnerDisplay?.hasContent);
  const extractingText = isCoach ? '正在提取教练层…' : '正在提取学习层…';

  return (
    <FloatingDetailCard
      title={isCoach ? '教练层详情' : '学习层详情'}
      subtitle={isCoach ? '卷面教练标注与教学提示' : '卷面学情证据与挣扎痕迹'}
      headerClassName={isCoach ? 'bg-[#F6E4D0]' : 'bg-[#D5F0EC]'}
      onClose={onClose}
      footer={onExtract ? (
        <button
          type="button"
          disabled={extracting}
          onClick={onExtract}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {extracting ? <Loader2 size={16} className="animate-spin" /> : null}
          {extracting
            ? extractingText
            : (isCoach ? '重新提取教练层' : '重新提取学习层')}
        </button>
      ) : null}
    >
      {extractError && !extracting ? <p className="mb-3 text-sm text-danger">{extractError}</p> : null}
      {extracting && !hasContent ? (
        <div className="flex items-center gap-2 text-sm text-primary">
          <Loader2 size={16} className="animate-spin" />
          {extractingText}
        </div>
      ) : (
        <OverlayDetailBody
          layer={layer}
          coachDisplay={coachDisplay}
          learnerDisplay={learnerDisplay}
        />
      )}
    </FloatingDetailCard>
  );
}
